import React, { useEffect, useMemo, useState } from 'react';
import { Skeleton } from '@mui/material';

import { DocumentListProps } from '../DocumentsList/types';
import { useDocumentsStore } from '../../../../stores/documentsStore';
import { useContextStore } from '../../../../stores/storeRoot';
import {
  StyledHeaderContainer,
  ViewAllWrapper,
  Wrapper,
} from '../DocumentsList/styles';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import {
  CropConnectCountry,
  CropConnectRoleDefinitions,
  DocumentSnapshotDto,
} from '../../../../services/cropConnectApiClient';
import ActionButton from '../../../components/ActionButton/ActionButton';
import SecurityTrim from '../../../../services/auth/SecurityTrim';
import {
  DocumentListHeader,
  DocumentListItem,
} from '../DocumentsList/DocumentsLists';

const DocumentListWithPrefilter: React.FC<DocumentListProps> = ({
  title,
  folder,
  pageSize,
  onToggleView,
  buttonText,
  showExtraButton,
  hideInvoiceColumn,
  hideCalendarYearColumn,
  prefilter,
}) => {
  const initialPageSize = pageSize ?? 10;
  const [documents, setDocuments] = useState<DocumentSnapshotDto[]>([]);
  const [showInvoiceNumberForCountry, setShowInvoiceNumberForCountry] =
    useState<boolean>(false);
  const [showCalendarYear, setShowCalendarYear] = useState<boolean>(false);
  const [totalElementsCount, setTotalElementsCount] = useState(0);
  const {
    isUpdating,
    isContextUpdating,
    isInitializing,
    getDocumentsPageWithPrefilter,
  } = useDocumentsStore();
  const { activeBpNumber, activeBpNumberCountry } = useContextStore();
  const { t } = useI18n();

  useEffect(() => {
    loadDocumentsPageIntern(0, initialPageSize);
  }, [activeBpNumber, initialPageSize]);

  useMemo(() => {
    setShowInvoiceNumberForCountry(
      !(
        hideInvoiceColumn ||
        activeBpNumberCountry?.toUpperCase() === CropConnectCountry.DE
      )
    );
    setShowCalendarYear(!hideCalendarYearColumn);
  }, [activeBpNumberCountry, hideInvoiceColumn, showCalendarYear]);

  const loadDocumentsPageIntern = async (
    pageIndex: number,
    pageSize: number
  ) => {
    try {
      const retrievalResult = await getDocumentsPageWithPrefilter(
        folder,
        prefilter!,
        pageSize,
        pageIndex * pageSize,
        'creationDate',
        true
      );
      if (!retrievalResult?.documents) {
        console.error('No documents returned from API');
      }
      setDocuments(retrievalResult?.documents ?? []);
      setTotalElementsCount(retrievalResult?.availableItemsCount ?? 0);
    } catch (e) {
      console.error('Error loading documents:', e);
      setDocuments([]);
      setTotalElementsCount(0);
    }
  };

  const handleViewAll = () => {
    const realisticPageSize = totalElementsCount || 1000;
    loadDocumentsPageIntern(0, realisticPageSize);
  };

  const renderItemRow = (item: DocumentSnapshotDto) => (
    <DocumentListItem
      key={item.id}
      id={item.id}
      invoiceNumber={
        item.additionalData ? item.additionalData!['InvoiceNumber'] : undefined
      }
      countryIsoCode={activeBpNumberCountry}
      title={item.name}
      campaignYear={item.campaignYear}
      creationDate={item.creationDate}
      read={item.read}
      showInvoiceNumber={showInvoiceNumberForCountry}
      showCalendarYear={showCalendarYear}
    />
  );

  const renderHeaderRow = () => (
    <DocumentListHeader
      title={title}
      showInvoiceNumber={showInvoiceNumberForCountry}
      showCalendarYear={showCalendarYear}
      onFullReportClick={onToggleView}
      buttonText={buttonText}
      showExtraButton={showExtraButton}
    />
  );

  return (
    <Wrapper>
      {isUpdating || isContextUpdating || isInitializing ? (
        <div className="skeleton-table">
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            animation="wave"
            style={{ borderRadius: '8px' }}
          />
          {Array.from({ length: 10 }).map((_, index) => (
            <div key={index} className="skeleton-row">
              <Skeleton
                variant="rectangular"
                width="60%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
              <Skeleton
                variant="rectangular"
                width="60%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
              <Skeleton
                variant="rectangular"
                width="60%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
              <Skeleton
                variant="rectangular"
                width="90%"
                height={40}
                animation="wave"
                style={{ borderRadius: '8px' }}
              />
            </div>
          ))}
        </div>
      ) : documents.length === 0 ? (
        <>
          <StyledHeaderContainer>
            <h3>{t(TranslationKeys.my_payments_documents_list_title)}</h3>
            {showExtraButton && (
              <SecurityTrim
                requiredBusinessPartnerRoles={[
                  CropConnectRoleDefinitions.Grower_DE,
                  CropConnectRoleDefinitions.ServiceProvider_DE,
                  CropConnectRoleDefinitions.Grower_Organic_DE,
                ]}
              >
                <ActionButton
                  variant="accept-blue"
                  buttonText={buttonText}
                  onClickAction={onToggleView}
                />
              </SecurityTrim>
            )}
          </StyledHeaderContainer>
          <p>{t(TranslationKeys.documents_list_no_documents_found)}</p>
        </>
      ) : (
        <>
          {renderHeaderRow()}
          {documents
            .filter((document) =>
              document.name
                ?.toLowerCase()
                .includes(prefilter?.toLowerCase() ?? '')
            )
            .map(renderItemRow)}
          {documents.length < totalElementsCount && (
            <ViewAllWrapper>
              <ActionButton
                variant="accept-blue-light"
                buttonText={t(TranslationKeys.documents_list_button_view_all)}
                onClickAction={handleViewAll}
              />
            </ViewAllWrapper>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default DocumentListWithPrefilter;
